import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import OpenInNewOutlined from "@mui/icons-material/OpenInNewOutlined";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import { DataGrid } from "@mui/x-data-grid";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { PHARMACY_HALO_COLORS } from "@/config/colors";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";
import { pharmacyHaloDataGridSx } from "@/utils/adminTableStyles";

const BLUE = PHARMACY_HALO_COLORS.BLUE;

function fmtDate(iso) {
  if (!iso) return "—";
  const p = String(iso).split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}` : iso;
}

function StatCard({ label, value, sub, highlight }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: highlight ? `2px solid ${BLUE}` : "1px solid #e5e7eb",
        backgroundColor: highlight ? "#eef1f9" : "#f9fafb",
        display: "flex",
        flexDirection: "column",
        gap: 0.25,
      }}
    >
      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, color: BLUE, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
}

const SMS_STATUS_LABELS = { 0: "Pending", 1: "Sending", 2: "Sent", 3: "Failed" };

const openActionColumn = (onOpen) => ({
  field: "actions",
  headerName: "",
  width: 52,
  sortable: false,
  filterable: false,
  disableColumnMenu: true,
  align: "center",
  headerAlign: "center",
  renderCell: (p) => (
    <Tooltip title="View full message">
      <IconButton size="small" onClick={() => onOpen(p.row)} sx={{ color: BLUE }}>
        <OpenInNewOutlined sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  ),
});

const getRecentSmsColumns = (onOpen) => [
  {
    field: "SentAt",
    headerName: "Date",
    width: 90,
    renderCell: (p) => <Typography variant="body2">{fmtDate(p.value)}</Typography>,
  },
  {
    field: "name",
    headerName: "Recipient",
    flex: 1,
    minWidth: 130,
    renderCell: (p) => (
      <Typography variant="body2" noWrap sx={{ width: 1 }}>
        {[p.row.FirstName, p.row.LastName].filter(Boolean).join(" ") || p.row.PhoneNumber || "—"}
      </Typography>
    ),
  },
  {
    field: "MessageText",
    headerName: "Message",
    flex: 2,
    minWidth: 180,
    renderCell: (p) => (
      <Typography variant="body2" noWrap sx={{ width: 1, color: "text.secondary" }}>
        {p.value || "—"}
      </Typography>
    ),
  },
  {
    field: "Status",
    headerName: "Status",
    width: 90,
    align: "center",
    headerAlign: "center",
    renderCell: (p) => {
      const label = SMS_STATUS_LABELS[p.value] ?? `${p.value}`;
      const color = p.value === 2 ? "success" : p.value === 3 ? "error" : "default";
      return <Chip label={label} size="small" color={color} variant="outlined" />;
    },
  },
  openActionColumn(onOpen),
];

const getBroadcastColumns = (onOpen) => [
  {
    field: "CreateDateTime",
    headerName: "Date",
    width: 90,
    renderCell: (p) => <Typography variant="body2">{fmtDate(p.value)}</Typography>,
  },
  {
    field: "Name",
    headerName: "Name",
    flex: 1,
    minWidth: 130,
    renderCell: (p) => (
      <Typography variant="body2" noWrap sx={{ width: 1, fontWeight: 600 }}>
        {p.value || "—"}
      </Typography>
    ),
  },
  {
    field: "StaffName",
    headerName: "Staff",
    width: 120,
    renderCell: (p) => (
      <Typography variant="body2" noWrap sx={{ width: 1 }}>
        {p.value || "—"}
      </Typography>
    ),
  },
  {
    field: "CustomerCount",
    headerName: "Recipients",
    width: 90,
    type: "number",
    align: "center",
    headerAlign: "center",
  },
  openActionColumn(onOpen),
];

const ChemistHaloTextTab = ({ chemistUUID, isManager = false }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [viewItem, setViewItem] = useState(null); // { type: "sms" | "broadcast", row }

  const fetchData = useCallback(async () => {
    if (!chemistUUID || data) return;
    try {
      setLoading(true);
      setError(null);
      const res = await phappsRequest(isManager ? API_PATHS.MANAGER_CHEMIST_HALOTEXT : API_PATHS.CHEMIST_HALOTEXT, "GET", null, { ChemistUUID: chemistUUID });
      setData(res);
    } catch (e) {
      setError(getAdminApiErrorMessage(e, "Failed to load HaloText data"));
    } finally {
      setLoading(false);
    }
  }, [chemistUUID, data]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return (
    <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
      <Typography color="textSecondary">Loading HaloText data…</Typography>
    </Box>
  );

  if (error) return (
    <Box sx={{ py: 4, px: 2 }}>
      <Alert severity="warning">{error}</Alert>
    </Box>
  );

  if (!data) return null;

  const { Metrics, SmsPerDay, RecentSms, RecentBroadcasts } = data;
  const avgPerDay = Metrics?.TotalSms30d ? (Number(Metrics.TotalSms30d) / 30).toFixed(1) : "0.0";

  const smsRows = (RecentSms ?? []).map((r, i) => ({ id: r.ContactLogID ?? i, ...r }));
  const broadcastRows = (RecentBroadcasts ?? []).map((r, i) => ({ id: r.MessageGroupID ?? i, ...r }));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {/* Stat cards */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 1.5 }}>
        <StatCard
          label="SMS sent (30d)"
          value={Number(Metrics?.TotalSms30d ?? 0).toLocaleString()}
          sub={`~${avgPerDay}/day avg`}
          highlight
        />
        <StatCard
          label="All-time SMS"
          value={Number(Metrics?.TotalSmsAllTime ?? 0).toLocaleString()}
        />
        {Metrics?.SMSDedicatedNumber && (
          <StatCard
            label="Dedicated Number"
            value={Metrics.SMSDedicatedNumber}
          />
        )}
      </Box>

      {/* Main layout: left col (chart + broadcasts stacked) | right col (SMS stretches to match) */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2, alignItems: "stretch" }}>

        {/* LEFT: chart then broadcasts */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

          {/* Chart card */}
          <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: BLUE, mb: 1 }}>
              SMS sent per day (30d)
            </Typography>
            <Box sx={{ height: 180 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={SmsPerDay} margin={{ top: 4, right: 8, left: -20, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 9 }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={fmtDate}
                    interval="preserveStartEnd"
                  />
                  <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <ReTooltip
                    formatter={(v) => [v, "SMS"]}
                    labelFormatter={(v) => `Date: ${fmtDate(v)}`}
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
                  />
                  <Bar dataKey="count" fill={BLUE} radius={[3, 3, 0, 0]} maxBarSize={28} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Box>

          {/* Broadcasts card */}
          <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: BLUE, mb: 1.5 }}>
              Recent broadcasts
            </Typography>
            {broadcastRows.length > 0 ? (
              <Box sx={{ height: 300 }}>
                <DataGrid
                  rows={broadcastRows}
                  columns={getBroadcastColumns((row) => setViewItem({ type: "broadcast", row }))}
                  density="compact"
                  disableColumnMenu
                  disableRowSelectionOnClick
                  hideFooter
                  sx={{ ...pharmacyHaloDataGridSx, height: "100%" }}
                />
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary">No broadcasts found.</Typography>
            )}
          </Box>
        </Box>

        {/* RIGHT: SMS list — fixed height matching left column total */}
        {/* Left total: chart card (240) + gap (16) + broadcasts card (364) = 620px          */}
        {/* SMS DataGrid height = 620 - card padding (32) - title row (32) = 556px           */}
        <Box sx={{
          p: 2, borderRadius: 2, border: "1px solid #e5e7eb",
          backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
        }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: BLUE, mb: 1.5 }}>
            Recent SMS (last 30)
          </Typography>
          {smsRows.length > 0 ? (
            <Box sx={{ height: 556 }}>
              <DataGrid
                rows={smsRows}
                columns={getRecentSmsColumns((row) => setViewItem({ type: "sms", row }))}
                density="compact"
                disableColumnMenu
                disableRowSelectionOnClick
                hideFooter
                sx={{ ...pharmacyHaloDataGridSx, height: "100%" }}
              />
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">No SMS sent in the last 30 days.</Typography>
          )}
        </Box>

      </Box>

      {/* Full message viewer */}
      <Dialog open={!!viewItem} onClose={() => setViewItem(null)} maxWidth="sm" fullWidth>
        {viewItem && (
          <>
            <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontWeight: 700, pr: 1.5 }}>
              {viewItem.type === "broadcast" ? (viewItem.row.Name || "Broadcast message") : "SMS message"}
              <IconButton size="small" onClick={() => setViewItem(null)} aria-label="Close">
                <CloseOutlined sx={{ fontSize: 18 }} />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Date: ${fmtDate(viewItem.type === "broadcast" ? viewItem.row.CreateDateTime : viewItem.row.SentAt)}`}
                />
                {viewItem.type === "broadcast" ? (
                  <>
                    {viewItem.row.StaffName && (
                      <Chip size="small" variant="outlined" label={`Sent by: ${viewItem.row.StaffName}`} />
                    )}
                    {viewItem.row.CustomerCount != null && (
                      <Chip size="small" variant="outlined" label={`Recipients: ${viewItem.row.CustomerCount}`} />
                    )}
                  </>
                ) : (
                  <>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`To: ${[viewItem.row.FirstName, viewItem.row.LastName].filter(Boolean).join(" ") || viewItem.row.PhoneNumber || "—"}`}
                    />
                    {viewItem.row.Status != null && (
                      <Chip
                        size="small"
                        variant="outlined"
                        color={viewItem.row.Status === 2 ? "success" : viewItem.row.Status === 3 ? "error" : "default"}
                        label={`Status: ${SMS_STATUS_LABELS[viewItem.row.Status] ?? viewItem.row.Status}`}
                      />
                    )}
                  </>
                )}
              </Box>
              <Box sx={{ p: 1.75, borderRadius: 2, backgroundColor: "#f9fafb", border: "1px solid #f3f4f6" }}>
                <Typography sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: "0.88rem", lineHeight: 1.65, color: "#374151" }}>
                  {(viewItem.type === "broadcast" ? viewItem.row.TemplateText : viewItem.row.MessageText) || "—"}
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setViewItem(null)} sx={{ textTransform: "none", fontWeight: 600 }}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default ChemistHaloTextTab;
